import type { OrderMarginPreviewPrice } from '../../api/orderMarginPreviewApi';
import { haveOrderDraftLinesChanged, type OrderDraftLine } from '../draft/orderDraftModel';

/**
 * Rules of the „Ceny sprzedaży” window (first mockup, `pricesModal`): one
 * price field per line; the margin beside it is the SERVER's
 * (`POST /orders/:id/margin-preview`, see `useOrderMarginPreview`) — this file
 * only decides what may be asked and whether the window can be saved.
 */

/** A price typed in the window; null = empty or not a positive amount. */
export const parseOrderPriceInput = (value: number | string | null | undefined): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const number = typeof value === 'number' ? value : Number(String(value).replace(/\s/g, '').replace(',', '.'));
  return Number.isFinite(number) && number > 0 ? number : null;
};

/** Every line has a positive price: „Zapisz ceny” is available (mockup: `ok`). */
export const arePricesComplete = (prices: Record<string, number | null>, lines: readonly OrderDraftLine[]): boolean =>
  lines.length > 0 && lines.every((line) => parseOrderPriceInput(prices[line.key]) !== null);

/** The window has nothing to ask the server while the lines are not as saved (apart from the price) or a price is missing. */
export type OrderPricesPreviewBlock = 'incomplete' | 'unsaved' | null;

/**
 * The prices to send to `margin-preview`, or why there is nothing to ask.
 * The server prices the SAVED lines: a line without an id, or a quantity /
 * unit / currency edited and not yet saved, would be priced wrongly, so the
 * window waits for „Zapisz szkic” instead of showing a figure that would not
 * match what the order shows after saving.
 */
export function getOrderPricesPreviewRequest(
  prices: Record<string, number | null>,
  lines: readonly OrderDraftLine[],
  savedLines: readonly OrderDraftLine[],
): { prices: OrderMarginPreviewPrice[] | null; block: OrderPricesPreviewBlock } {
  if (lines.length === 0 || lines.some((line) => !line.id)) return { prices: null, block: 'unsaved' };
  // Only the price may differ from the saved lines.
  const withSavedPrices = lines.map((line) => {
    const saved = savedLines.find((row) => row.id === line.id);
    return saved ? { ...line, unitPrice: saved.unitPrice, totalPrice: saved.totalPrice } : line;
  });
  if (haveOrderDraftLinesChanged(savedLines, withSavedPrices)) return { prices: null, block: 'unsaved' };
  if (!arePricesComplete(prices, lines)) return { prices: null, block: 'incomplete' };
  return {
    prices: lines.map((line) => ({ id: String(line.id), unitPrice: parseOrderPriceInput(prices[line.key]) as number })),
    block: null,
  };
}
